import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IoMdEye, IoMdEyeOff } from "react-icons/io";
import { MdPerson, MdEmail, MdPhone, MdStorefront, MdLocationOn, MdLock, MdCheck } from "react-icons/md";
import AuthLayout from './AuthLayout';
import InputBox from '../common/InputBox';
import { useRestaurantNav } from '../../hooks/useRestaurantNav';

// TypeScript interfaces
interface SignupInfo {
  name: string;
  email: string;
  phone: string;
  companyName: string;
  address: string;
  password: string;
  confirmPassword: string;
}

interface Feature {
  id: string;
  icon: React.ReactNode;
  title: string;
  description: string;
}

const Signup: React.FC = () => {
  const [signupInfo, setSignupInfo] = useState<SignupInfo>({
    name: '',
    email: '',
    phone: '',
    companyName: '',
    address: '',
    password: '',
    confirmPassword: ''
  });

  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);
  const [selectedFeatures, setSelectedFeatures] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');

const { navigate } = useRestaurantNav();

  const features: Feature[] = [
    { 
      id: 'promoflow', 
      icon: <MdStorefront size={20} />, 
      title: 'PromoFlow',
      description: 'Automated promotional campaigns'
    },
    { 
      id: 'menubuilder', 
      icon: <MdStorefront size={20} />, 
      title: 'Menu Builder',
      description: 'Digital menu customization'
    },
    { 
      id: 'commerce', 
      icon: <MdStorefront size={20} />, 
      title: 'Commerce AI',
      description: 'AI-powered business insights'
    },
    { 
      id: 'voice', 
      icon: <MdStorefront size={20} />, 
      title: 'Voice Integration',
      description: 'Voice recognition capabilities'
    },
  ];

  const handleInputChange = (field: keyof SignupInfo) => (value: string) => {
    setSignupInfo((prevInfo) => ({ ...prevInfo, [field]: value }));
    setError('');
  };

  const handleFeatureToggle = (featureId: string) => {
    setSelectedFeatures(prev =>
      prev.includes(featureId)
        ? prev.filter(id => id !== featureId)
        : [...prev, featureId]
    );
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (signupInfo.password !== signupInfo.confirmPassword) {
      setError('Passwords do not match!');
      return;
    }

    if (selectedFeatures.length === 0) {
      setError('Please select at least one feature');
      return;
    }

    setLoading(true);
    // Add your signup API logic here
    console.log('Signup Info:', signupInfo, 'Features:', selectedFeatures);
    
    // Simulate API call
    setTimeout(() => {
      setLoading(false);
      navigate('/login', { state: { message: 'Account created successfully! Please login.' } });
    }, 1500);
  };

  return (
    <AuthLayout
      title="Join PromoFlow AI"
      subtitle="Starting with one idea."
    >
      <div className="w-full max-w-md h-full flex items-center justify-center">
        {/* Form Card with Scrollable Content */}
        <div className="backdrop-blur-xl border border-gray-700/50 rounded-2xl shadow-2xl flex flex-col overflow-hidden" style={{ maxHeight: '78vh', width: '100%' }}>
          {/* Header - Fixed */}
          <div className="py-8 px-">
            <h2 className="text-white text-center text-xl font-medium mb-5">
              Create Your Account
            </h2>
            
            <div className="w-full h-px bg-gradient-to-r from-transparent via-orange-500 to-transparent"></div>
          </div>
          
          {/* Scrollable Form Content */}
          <div className="flex-1 overflow-y-auto px-6 sm:px-8">
            <form id="signup-form" onSubmit={handleSubmit} className="space-y-4">
              {/* Personal Details */}
              <div className="space-y-4">

                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label className="block text-gray-300 font-light text-xs mb-2">Full Name</label>
                    <InputBox
                      type="text"
                      id="name"
                      placeholder="John Doe"
                      onChange={handleInputChange('name')}
                      value={signupInfo.name}
                      required={true}
                      className="w-full bg-transparent border border-gray-700 px-4 py-2.5 pl-12 text-white placeholder:text-gray-500 focus:outline-none focus:border-orange-500 hover:border-orange-500 transition-colors duration-300"
                      style={{ borderRadius: '34px' }}
                      icon={<MdPerson size={20} />}
                    />
                  </div>

                  <div>
                    <label className="block text-gray-300 font-light text-xs mb-3">Email</label>
                    <InputBox
                      type="email"
                      id="email"
                      placeholder="name@restaurant.com"
                      onChange={handleInputChange('email')}
                      value={signupInfo.email}
                      required={true}
                      className="w-full bg-transparent border border-gray-700 px-4 py-3.5 pl-12 text-white placeholder:text-gray-500 focus:outline-none focus:border-orange-500 hover:border-orange-500 transition-colors duration-300"
                      style={{ borderRadius: '34px' }}
                      icon={<MdEmail size={20} />}
                    />
                  </div>

                  <div>
                    <label className="block text-gray-300 font-light text-xs mb-3">Phone</label>
                    <InputBox
                      type="tel"
                      id="phone"
                      placeholder="+1 (555) 000-0000"
                      onChange={handleInputChange('phone')}
                      value={signupInfo.phone}
                      required={true}
                      className="w-full bg-transparent border border-gray-700 px-4 py-3.5 pl-12 text-white placeholder:text-gray-500 focus:outline-none focus:border-orange-500 hover:border-orange-500 transition-colors duration-300"
                      style={{ borderRadius: '34px' }}
                      icon={<MdPhone size={20} />}
                    />
                  </div>

                  <div>
                    <label className="block text-gray-300 font-light text-xs mb-3">Company Name</label>
                    <InputBox
                      type="text"
                      id="companyName"
                      placeholder="Your Restaurant"
                      onChange={handleInputChange('companyName')}
                      value={signupInfo.companyName}
                      required={true}
                      className="w-full bg-transparent border border-gray-700 px-4 py-3.5 pl-12 text-white placeholder:text-gray-500 focus:outline-none focus:border-orange-500 hover:border-orange-500 transition-colors duration-300"
                      style={{ borderRadius: '34px' }}
                      icon={<MdStorefront size={20} />}
                    />
                  </div>

                  <div>
                    <label className="block text-gray-300 font-light text-xs mb-3">Business Address</label>
                    <div className="relative">
                      <div className="absolute left-4 top-4 text-orange-500">
                        <MdLocationOn size={20} />
                      </div>
                      <textarea
                        id="address"
                        placeholder="123 Main Street, City, State"
                        value={signupInfo.address}
                        onChange={(e) => handleInputChange('address')(e.target.value)}
                        required
                        rows={2}
                        className="w-full bg-transparent border border-gray-700 px-4 py-3.5 pl-12 text-white placeholder:text-gray-500 focus:outline-none focus:border-orange-500 hover:border-orange-500 transition-colors duration-300 resize-none"
                        style={{ borderRadius: '24px' }}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-gray-300 font-light text-xs mb-3">Password</label>
                    <div className="relative">
                      <InputBox
                        type={showPassword ? 'text' : 'password'}
                        id="password"
                        placeholder="Create password"
                        onChange={handleInputChange('password')}
                        value={signupInfo.password}
                        required={true}
                        className="w-full bg-transparent border border-gray-700 px-4 py-3.5 pl-12 pr-12 text-white placeholder:text-gray-500 focus:outline-none focus:border-orange-500 hover:border-orange-500 transition-colors duration-300"
                        style={{ borderRadius: '34px' }}
                        icon={<MdLock size={20} />}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-4 top-1/2 transform -translate-y-1/2 text-orange-500"
                      >
                        {showPassword ? <IoMdEyeOff size={22} /> : <IoMdEye size={22} />}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-gray-300 font-light text-xs mb-3">Confirm Password</label>
                    <div className="relative">
                      <InputBox
                        type={showConfirmPassword ? 'text' : 'password'}
                        id="confirmPassword"
                        placeholder="Confirm password"
                        onChange={handleInputChange('confirmPassword')}
                        value={signupInfo.confirmPassword}
                        required={true}
                        className="w-full bg-transparent border border-gray-700 px-4 py-3.5 pl-12 pr-12 text-white placeholder:text-gray-500 focus:outline-none focus:border-orange-500 hover:border-orange-500 transition-colors duration-300"
                        style={{ borderRadius: '34px' }}
                        icon={<MdLock size={20} />}
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-4 top-1/2 transform -translate-y-1/2 text-orange-500"
                      >
                        {showConfirmPassword ? <IoMdEyeOff size={22} /> : <IoMdEye size={22} />}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {error && (
                <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-center mb-4">
                  {error}
                </div>
              )}
            </form>
          </div>

          {/* Sticky Footer with Button - Always visible */}
          <div className="p-6 sm:p-8 pt-4 border-t border-gray-700/50 bg-[#212121]">
            {/* Submit Button */}
            <div className="flex justify-center mb-4">
              <button
                type="submit"
                form="signup-form"
                disabled={loading}
                style={{ borderRadius: '34px' }}
                className={`px-12 py-2.5 font-normal transition-colors duration-300 ${
                  loading 
                    ? 'bg-gray-600 text-gray-400 cursor-not-allowed' 
                    : 'bg-gradient-to-r from-orange-500 to-orange-600 text-white hover:from-orange-600 hover:to-orange-700'
                }`}
              >
                {loading ? 'Creating Account...' : 'Create Account'}
              </button>
            </div>

            {/* Sign In Link */}
            <div className="text-center">
              <p className="text-xs text-gray-400">
                Already have an account?{' '}
                <button
                  type="button"
                  onClick={() => navigate('/login')}
                  className="text-orange-500 hover:text-orange-400 font-semibold transition-colors hover:underline"
                >
                  Sign In
                </button>
              </p>
            </div>
          </div>
        </div>
      </div>
    </AuthLayout>
  );
};

export default React.memo(Signup);
